import { motion } from "framer-motion";
import { Award, Target, Users, Zap, TrendingUp, Shield, Clock, CheckCircle } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function About() {
  const milestones = [
    { year: "2020", title: "Company Founded", description: "Started with a vision to revolutionize financial services" },
    { year: "2022", title: "AI Integration", description: "Pioneered AI-powered credit analysis and tax optimization" },
    { year: "2024", title: "1000+ Clients", description: "Reached milestone of serving over 1000 satisfied clients" },
    { year: "2026", title: "Industry Leader", description: "Recognized as top AI-powered financial services provider" },
  ];

  const values = [
    {
      icon: Shield,
      title: "Trust & Transparency",
      description: "We operate with complete honesty and clarity in all client interactions",
    },
    {
      icon: Zap,
      title: "Innovation First",
      description: "Leveraging cutting-edge AI to deliver superior results faster",
    },
    {
      icon: Users,
      title: "Client-Centric",
      description: "Your success is our success - we're invested in your financial journey",
    },
    {
      icon: Target,
      title: "Results Driven",
      description: "Focused on measurable outcomes and tangible improvements",
    },
  ];

  const certifications = [
    "Certified Credit Consultant (CCC)",
    "Enrolled Agent (EA) - IRS",
    "Certified Business Consultant (CBC)",
    "AI Ethics & Compliance Certified",
  ];

  const whyChooseUs = [
    {
      icon: TrendingUp,
      title: "Proven Track Record",
      description: "95% client satisfaction rate with average credit score improvement of 120+ points",
    },
    {
      icon: Zap,
      title: "AI-Powered Efficiency",
      description: "Our proprietary AI systems deliver results 3x faster than traditional methods",
    },
    {
      icon: Users,
      title: "Personalized Service",
      description: "Every client receives a customized strategy tailored to their unique situation",
    },
    {
      icon: Clock,
      title: "24/7 Support",
      description: "AI-assisted support available around the clock for your peace of mind",
    },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_BG_8}
            alt="About Hero"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight">
              About Elevation Systems AI
            </h1>
            <p className="text-xl text-muted-foreground">
              Credit To Millions — Transforming financial futures through AI-powered credit, tax, and funding strategies
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Elevation Systems AI was born from one simple belief: everyone deserves a clear path from where they are today to the financial future they've always imagined. We built Credit To Millions to be that path — a complete system that takes you from credit challenges to building real wealth.
                </p>
                <p>
                  Our founder recognized that most people don't fail financially because they lack ambition. They fail because no one ever showed them the blueprint. Traditional financial services are fragmented, expensive, and gatekept. We tore that system down and rebuilt it — AI-powered, integrated, and accessible.
                </p>
                <p>
                  Today, we're proud to serve clients nationwide through elevationsystemsai.com — helping them repair credit, save thousands in taxes, access business funding, and build the kind of wealth they once only dreamed about.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="relative"
            >
              <img
                src={IMAGES.CONSULTING_7}
                alt="Our Team"
                className="rounded-2xl shadow-lg w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Mission</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              To empower individuals and businesses with AI-enhanced financial services that are accessible, transparent, and results-driven. We believe everyone deserves expert guidance backed by the latest technology.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-card p-8 rounded-2xl border border-border hover:border-primary/50 transition-all duration-200"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Meet Our Founder</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Led by an expert with deep expertise across credit repair, tax services, and business consulting
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto bg-background p-8 md:p-12 rounded-2xl border border-border"
          >
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-4">
                <h3 className="text-2xl font-bold">Expert Leadership</h3>
                <p className="text-muted-foreground">
                  With over a decade of experience in financial services, our founder has built a reputation for excellence in credit repair, tax preparation, and business consulting. Her unique approach combines deep industry knowledge with innovative AI technology to deliver unparalleled results.
                </p>
                <p className="text-muted-foreground">
                  Specializing in complex credit situations, strategic tax planning, and business growth strategies, she has helped hundreds of clients transform their financial futures. Her commitment to personalized service and cutting-edge solutions has made our company a trusted partner for individuals and businesses alike.
                </p>
                <div className="pt-4">
                  <h4 className="font-semibold mb-3">Certifications & Credentials:</h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-48 h-48 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                  <Users className="w-24 h-24 text-primary" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Our Journey</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Key milestones that shaped our company and defined our commitment to excellence
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border" />
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-12"
              >
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                    className="relative pl-20"
                  >
                    <div className="absolute left-0 w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold shadow-lg">
                      {milestone.year}
                    </div>
                    <div className="bg-card p-6 rounded-2xl border border-border">
                      <h3 className="text-xl font-semibold mb-2">{milestone.title}</h3>
                      <p className="text-muted-foreground">{milestone.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              What sets us apart in the financial services industry
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
          >
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-background p-8 rounded-2xl border border-border hover:border-primary/50 transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto text-center bg-gradient-to-br from-primary/10 to-accent/10 p-12 rounded-2xl border border-primary/20"
          >
            <Award className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Financial Future?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of satisfied clients who have achieved their financial goals with our AI-powered services
            </p>
            <a
              href="#contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:scale-105 active:scale-95 transition-all duration-200 shadow-lg"
            >
              Schedule Your Free Consultation
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}